<script setup lang="ts">
import { reactive, computed } from 'vue'
import { IQuestionsResultContentState, IQuestionsResultApiDetail } from '@/types/common/questions'
import PhotographerCard from '@/components/molecules/PhotographerCard/index.vue'
import InvoiceIcon from '@/components/atoms/invoice/Icon/index.vue'
import { ComputableObject } from '@/types/global'

const props = defineProps<{
  result: IQuestionsResultApiDetail
  categorySlug: string
  restart: () => void
}>()
const states = reactive<ComputableObject<IQuestionsResultContentState>>({
  cards: computed(() =>
    props.result.photographers.map((photographer) => ({
      nickname: photographer.name,
      photo: photographer.mainImageUrl,
      facePhoto: photographer.faceImageUrl,
      ratingAvg: photographer.ratingAvg,
      isInvoicePhotographer: photographer.isInvoicePhotographer,
      price: photographer.price,
      holidayPrice: photographer.holidayPrice,
      detailUrl: `/photographers/${photographer.slug}?category=${props.categorySlug}`,
      isDetailLinkTargetBlank: true,
    }))
  ),
})
</script>

<template>
  <div class="content-title">診断結果</div>
  <div v-if="props.result.isRecommend === false || states.cards.length === 0" class="not-found-message">
    該当するフォトグラファーが見つかりませんでした。
  </div>
  <div v-if="states.cards.length > 0">
    <div class="cards-caption">
      <span v-if="props.result.isRecommend">
        <b
          ><span class="large">{{ states.cards.length }}</span
          >人</b
        >のフォトグラファーをおすすめします。</span
      >
      <span v-else>
        <b class="large">{{ props.result.selectedPref }}</b
        >で活動中のフォトグラファーをご案内いたします。</span
      >
    </div>
    <div class="invoice-text"><InvoiceIcon :size="'small'" />…このフォトグラファーは適格請求書発行事業者です。</div>
    <div class="cards">
      <div v-for="(card, key) in states.cards" :key="key">
        <PhotographerCard :card="card">
          <div class="photographer-card-content">
            <div v-if="props.result.isRecommend">
              <div class="photographer-card-content-title"><i class="op-icon op-icon-check"></i>選択した内容</div>
              <div class="photographer-card-content-info">
                撮影ジャンル：{{ props.result.selectedCategory }}<br />
                撮影場所：{{ props.result.selectedPref }}
              </div>
            </div>
            <div class="photographer-card-content-button">フォトグラファー詳細を見る</div>
          </div>
        </PhotographerCard>
      </div>
    </div>
  </div>
  <button type="button" class="back" @click="props.restart()">
    もう一度診断する<img
      class="back-icon"
      :src="'/assets/images/iconmonstr-arrow-right-circle-lined-white.svg'"
      alt=""
    />
  </button>
</template>

<style lang="scss" scoped>
.content-title {
  text-align: center;
  font-weight: 600;
  font-size: 32px;
  letter-spacing: 1.6px;
  margin-bottom: 0;
  line-height: 1;
  @media (max-width: 767px) {
    font-size: 24px;
  }
}
.not-found-message,
.cards-caption {
  margin: 40px auto 0;
  width: fit-content;
  text-align: left;
  font-size: 16px;
  @media (max-width: 767px) {
    margin-top: 30px;
    font-size: 14px;
  }
}
.not-found-message {
  margin-top: 40px;
  margin-bottom: 30px;
  @media (max-width: 767px) {
    margin-top: 30px;
    margin-bottom: 15px;
  }
}
.cards-caption {
  .large {
    font-size: 24px;
    @media (max-width: 767px) {
      font-size: 20px;
    }
  }
}
.cards {
  display: grid;
  margin-top: 20px;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 20px;
  @media (max-width: 767px) {
    margin-top: 10px;
    grid-template-columns: auto;
  }
}
.not-found {
  margin: 0 auto;
  font-size: 16px;
  @media (max-width: 767px) {
    text-align: left;
  }
}
.back {
  position: relative;
  display: block;
  width: 360px;
  height: 48px;
  margin: 50px auto 0;
  font-size: 18px;
  text-align: center;
  background-color: #333;
  box-shadow: 0px 2px 6px #0000001a;
  border-radius: 24px;
  color: #fff;
  line-height: 48px;
  text-decoration: none;
  cursor: pointer;
  @media (max-width: 767px) {
    width: 100%;
    margin-top: 40px;
    font-size: 16px;
  }
}
.back-icon {
  position: absolute;
  top: 50%;
  right: 20px;
  width: 18px;
  height: 18px;
  transform: translateY(-50%);
  @media (max-width: 767px) {
    right: 15px;
  }
}
.photographer-card {
  &-content {
    padding: 15px 0;
    color: #333;
    &-title {
      display: flex;
      align-items: center;
      font-weight: 600;
      i {
        margin-right: 5px;
      }
    }
    &-info {
      margin-top: 10px;
      margin-bottom: 20px;
    }
    &-button {
      width: 100%;
      height: 46px;
      font-size: 16px;
      font-weight: 600;
      text-align: center;
      text-decoration: none;
      border: solid 1px #333;
      border-radius: 23px;
      line-height: 46px;
    }
  }
}
.invoice-text {
  display: flex;
  align-items: center;
  margin-top: 30px;
  font-size: 12px;
  gap: 3px;
  @media (max-width: 767px) {
    margin-top: 10px;
    justify-content: center;
  }
}
</style>
